import React, { useState } from 'react';
import { TFunction } from 'react-i18next';
import { CustomDialog, Button } from 'web-library';
import { RouteComponentProps, useHistory } from 'react-router-dom';
import { MutationStatus, UseMutateFunction } from 'react-query';
import { ButtonVariant } from 'web-library/stories/atom/Button/Button';
import { AxiosError, AxiosResponse } from 'axios';
import BankTransferModal from '../BankTransferModal';
import PaymentApiButton, { PaymentMethod } from './PaymentApiButton';
import { ExamPaymentModeKey } from '../../../../api-refactored/group-exam-registration/usePostReviewGroupExamRegistration';
import { PaymentErrorAction } from './index';
import { GenericErrorResponse } from '../../../../api-refactored/typings';

interface IDifferentPaymentModeCallback {
  coordinatorExamPaymentMode?: ExamPaymentModeKey;
  onHandlePaymentMethod: (callback: (txnId: string) => void, paymentMethod?: string) => void;
  zeroDollarPaymentOption?: boolean;
  setIsPaymentModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedPaymentMethod: React.Dispatch<React.SetStateAction<PaymentMethod | null>>;
  setIsBankTransferModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  history: RouteComponentProps['history'];
}

const differentPaymentModeCallback = ({
  coordinatorExamPaymentMode,
  onHandlePaymentMethod,
  zeroDollarPaymentOption,
  setIsPaymentModalOpen,
  setSelectedPaymentMethod,
  setIsBankTransferModalOpen,
  history,
}: IDifferentPaymentModeCallback) => {
  if (coordinatorExamPaymentMode === ExamPaymentModeKey.bankTransferOption) {
    setIsBankTransferModalOpen(true);
  } else if (coordinatorExamPaymentMode === ExamPaymentModeKey.candidatePayOption) {
    onHandlePaymentMethod(
      (route: string) => history.push(route, { selectedPaymentMode: coordinatorExamPaymentMode }),
      'PAYMENT_LINK'
    );
  } else if (
    coordinatorExamPaymentMode === ExamPaymentModeKey.noPaymentModeSelected &&
    zeroDollarPaymentOption
  ) {
    onHandlePaymentMethod(
      (route: string) => history.push(route, { selectedPaymentMode: coordinatorExamPaymentMode }),
      ''
    );
  } else {
    setIsPaymentModalOpen(true);
    setSelectedPaymentMethod(null);
  }
};

interface PaymentModalsProps {
  isConfirmationModalOpen: boolean;
  setIsConfirmationModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  customConfirmationModalContent?: TFunction<'translation'>;
  onHandlePaymentMethod: (callback: (txnId: string) => void, paymentMethod?: string) => void;
  offlinePaymentMutate: UseMutateFunction<AxiosResponse, AxiosError<GenericErrorResponse>, string>;
  coordinatorExamPaymentMode?: ExamPaymentModeKey;
  zeroDollarPaymentOption?: boolean;
  setIsPaymentModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedPaymentMethod: React.Dispatch<React.SetStateAction<PaymentMethod | null>>;
  onClickPaymentButton: (callback: (hasConfirmModal?: boolean) => void) => void;
  onSuccessPayment: (txnId: string) => void;
  paymentErrorDispatch: React.Dispatch<PaymentErrorAction>;
  statusPaymentOffline: MutationStatus;
  buttonText?: string;
  fullWidth?: boolean;
  buttonVariant?: ButtonVariant;
  setIsDisabled: React.Dispatch<React.SetStateAction<boolean>>;
  UNEXPECTED_ERROR_ACTION: PaymentErrorAction;
  disabled?: boolean;
}

const PaymentModals = ({
  isConfirmationModalOpen,
  setIsConfirmationModalOpen,
  customConfirmationModalContent,
  onHandlePaymentMethod,
  offlinePaymentMutate,
  coordinatorExamPaymentMode,
  zeroDollarPaymentOption,
  setIsPaymentModalOpen,
  setSelectedPaymentMethod,
  onClickPaymentButton,
  onSuccessPayment,
  paymentErrorDispatch,
  statusPaymentOffline,
  buttonText,
  fullWidth,
  buttonVariant,
  setIsDisabled,
  UNEXPECTED_ERROR_ACTION,
  disabled,
}: PaymentModalsProps) => {
  const [isBankTransferModalOpen, setIsBankTransferModalOpen] = useState(false);
  const history = useHistory();

  return (
    <>
      <CustomDialog
        open={isConfirmationModalOpen}
        title="Confirm Registration"
        message={customConfirmationModalContent?.toString()}
        action={
          <>
            <Button
              id="button-back"
              type="button"
              variant="text"
              color="primary"
              onClick={() => setIsConfirmationModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              id="button-confirm-proceed"
              type="button"
              variant="text"
              color="primary"
              onClick={() => {
                setIsConfirmationModalOpen(false);
                differentPaymentModeCallback({
                  coordinatorExamPaymentMode,
                  onHandlePaymentMethod,
                  zeroDollarPaymentOption,
                  setIsPaymentModalOpen,
                  setSelectedPaymentMethod,
                  setIsBankTransferModalOpen,
                  history,
                });
              }}
            >
              Confirm and Proceed
            </Button>
          </>
        }
      />

      <BankTransferModal
        modelOpen={isBankTransferModalOpen}
        onClose={() => {
          setIsBankTransferModalOpen(false);
          setIsConfirmationModalOpen(true);
        }}
        onClickCheckout={() => {
          setIsBankTransferModalOpen(false);
          onHandlePaymentMethod((txnId: string) => {
            if (txnId) {
              offlinePaymentMutate(txnId, {
                onSuccess: () => {
                  onSuccessPayment(txnId);
                },
                onError: () => {
                  paymentErrorDispatch(UNEXPECTED_ERROR_ACTION);
                },
              });
            } else {
              paymentErrorDispatch(UNEXPECTED_ERROR_ACTION);
            }
          }, PaymentMethod.BANK_TRANSFER);
        }}
        statusPaymentOffline={statusPaymentOffline}
      />
      <PaymentApiButton
        onClickPaymentButton={onClickPaymentButton}
        disabled={disabled}
        buttonText={buttonText}
        fullWidth={fullWidth}
        buttonVariant={buttonVariant}
        setStateHandler={{
          setIsDisabled,
          setIsPaymentModalOpen,
          setSelectedPaymentMethod,
          setIsConfirmationModalOpen,
        }}
      />
    </>
  );
};

export default PaymentModals;
